import type { Image } from "../../aysa_api/models/image.types";
import type { LinkTarget } from "../../aysa_api/models/link_target.types";
import type { Video } from "../../aysa_api/models/video.types";

// TextAlignment defines the text alignment inside a text block.
export enum ContentBlocks_TextAlignment {
  UNSPECIFIED = 0,
  START = 1,
  CENTER = 2,
  END = 3,
  JUSTIFY = 4,
}

export enum ContentBlocks_Position {
  UNSPECIFIED = 0,
  START = 1,
  CENTER = 2,
  END = 3,
}

// Size defines a generic font size.
export enum ContentBlocks_Size {
  UNSPECIFIED = 0,
  XS = 1,
  S = 2,
  M = 3,
  L = 4,
  XL = 5,
}

// Style defines a set of abstract element styles.
export enum ContentBlocks_Style {
  UNSPECIFIED = 0,
  PRIMARY = 1,
  SECONDARY = 2,
  TEXT_LINK = 3,
}

// LayoutType defines the order of image and text elements.
export enum ContentBlocks_ImageTextModule_LayoutType {
  UNSPECIFIED = 0,
  IMAGE_TEXT = 1,
  TEXT_IMAGE = 2,
}

export interface ContentBlocks {}

// Header image with headline and logo.
// https://aboutyou.atlassian.net/wiki/spaces/CTC/pages/962494715/Custom+Pages+-+Content+Blocks#Header
export interface ContentBlocks_Header {
  logo?: Image;
  headline: ContentBlocks_TextBlock;
  subline?: ContentBlocks_TextBlock;
  backgroundColor?: string;
  image: Image;
}

// Headline with a logo.
// https://aboutyou.atlassian.net/wiki/spaces/CTC/pages/962494715/Custom+Pages+-+Content+Blocks#Headline
export interface ContentBlocks_Headline {
  text: ContentBlocks_TextBlock;
  backgroundColor?: string;
  logo?: Image;
}

// TextModule is a block of text with some styling properties.
// https://aboutyou.atlassian.net/wiki/spaces/CTC/pages/962494715/Custom+Pages+-+Content+Blocks#Text
export interface ContentBlocks_TextModule {
  text: ContentBlocks_TextBlock;
  backgroundColor?: string;
  position: ContentBlocks_Position;
}

// TextBlock is simpler version of TextModule and shared over various content elements.
export interface ContentBlocks_TextBlock {
  text: string;
  color: string;
  size: ContentBlocks_Size;
  alignment: ContentBlocks_TextAlignment;
}

// CTA is a basically a button with some styling properties.
// https://aboutyou.atlassian.net/wiki/spaces/CTC/pages/962494715/Custom+Pages+-+Content+Blocks#Single-CTA
export interface ContentBlocks_CTA {
  text: ContentBlocks_CTA_Text;
  link: LinkTarget;
  backgroundColor?: string;
  position: ContentBlocks_Position;
}

export interface ContentBlocks_CTA_Text {
  text: string;
  style: ContentBlocks_Style;
  color?: string;
}

export interface ContentBlocks_ImageVideo {
  imageOrVideo?:
    | {
        $case: "image";
        image: Image;
      }
    | {
        $case: "video";
        video: Video;
      };
}

// ImageTextModule contains an image with a headline and a text block.
// Content blocks are called modules if they combine an image with text.
export interface ContentBlocks_ImageTextModule {
  layout: ContentBlocks_ImageTextModule_LayoutType;
  headline?: ContentBlocks_TextBlock;
  text: ContentBlocks_TextBlock;
  backgroundColor?: string;
  imageVideo: ContentBlocks_ImageVideo;
  firstCta?: ContentBlocks_CTA;
  secondCta?: ContentBlocks_CTA;
}

// ThreeColumnsModule defines three columns with each containing an image, a headline and text.
export interface ContentBlocks_ThreeColumnsModule {
  first: ContentBlocks_ThreeColumnsModule_Column;
  second: ContentBlocks_ThreeColumnsModule_Column;
  third: ContentBlocks_ThreeColumnsModule_Column;
  backgroundColor?: string;
}

export interface ContentBlocks_ThreeColumnsModule_Column {
  headline?: ContentBlocks_TextBlock;
  text?: ContentBlocks_TextBlock;
  firstCta?: ContentBlocks_CTA;
  secondCta?: ContentBlocks_CTA;
  image: Image;
}

// ImageTile is an image with a headline and a CTA.
// https://aboutyou.atlassian.net/wiki/spaces/CTC/pages/962494715/Custom+Pages+-+Content+Blocks#Image-Tile
export interface ContentBlocks_ImageTile {
  cta?: ContentBlocks_CTA;
  headline?: ContentBlocks_TextBlock;
  backgroundColor?: string;
  image: Image;
}

// VideoTile is a video with a CTA.
// https://aboutyou.atlassian.net/wiki/spaces/CTC/pages/962494715/Custom+Pages+-+Content+Blocks#Video-Tile
export interface ContentBlocks_VideoTile {
  video: Video;
  cta?: ContentBlocks_CTA;
}

export interface ContentBlocks_DoubleTile {
  first: ContentBlocks_DoubleTile_Tile;
  second: ContentBlocks_DoubleTile_Tile;
  backgroundColor?: string;
}

export interface ContentBlocks_DoubleTile_Tile {
  cta?: ContentBlocks_CTA;
  imageVideo: ContentBlocks_ImageVideo;
}

export interface ContentBlocks_AssetSlider {
  headline?: ContentBlocks_TextBlock;
  backgroundColor?: string;
  slides: ContentBlocks_AssetSlider_Slide[];
}

export interface ContentBlocks_AssetSlider_Slide {
  imageVideo: ContentBlocks_ImageVideo;
  headline?: ContentBlocks_TextBlock;
  text?: ContentBlocks_TextBlock;
  cta?: ContentBlocks_CTA;
  // id example: 1WIolf
  id: string;
}

export interface ContentBlocks_NewsletterPushSignup {
  headline: string;
  subline?: string;
  cta: string;
  image: Image;
  isSubscribed: boolean;
}
